import type {
  ActivityRow,
  ActivitySortKey,
  FraudFlagRow,
  LeaderboardRow,
  Page,
  PlanogramChannelRow,
  PlanogramReportRow,
  PlanVsActual,
  PriceIndexRegionRow,
  PriceIndexRow,
  ReportSummary,
  VisitTrendPoint,
} from '@fieldvision/contracts';
import { api, can, useAccessScope, withQuery } from '@fieldvision/web-core';
import { useQuery } from '@tanstack/react-query';

import { daysBetween, startOfDayIso, toDateInput } from '../../lib/format.js';
import { filterKey, toQuery, useReportFilters, windowError } from '../../lib/report-filters.store.js';

/**
 * Every query the dashboard makes, in one place.
 *
 * THE REQUEST BUDGET IS THE POINT OF THIS FILE. Eleven concurrent requests
 * against the shared primary, on the screen every manager lands on, is precisely
 * the contention TSP 4.3 names as the likely first production incident. Spread
 * across eleven components nobody would ever see the total. Four mitigations,
 * applied uniformly here:
 *
 *   1. `belowFold` defers rows 4–6 until they are scrolled into view, taking the
 *      above-the-fold cost from eleven requests to five. By far the biggest win.
 *   2. `placeholderData` keeps the previous answer on screen while the next one
 *      loads, so changing the date does not blank six panels and jump the layout.
 *   3. `staleTime` of two minutes, double the global default, so bouncing between
 *      /overview and /reports does not refire everything.
 *   4. Exactly ONE polling query — the Activity Monitor, at 30 seconds. If a
 *      second interval ever appears on this page, it was not added on purpose.
 *
 * Every query is additionally gated on the permission its endpoint enforces, so
 * a supervisor without `report:read` sees one fewer panel rather than a red
 * error box on a screen that is working correctly.
 */

const SUMMARY_STALE = 120_000;
const keepPrevious = <T,>(previous: T | undefined) => previous;

export function useOverviewData({ belowFold }: { belowFold: boolean }) {
  const scope = useAccessScope();
  const filters = useReportFilters();

  const invalid = windowError(filters);
  const enabled = invalid === null;
  const key = filterKey(filters);
  const query = toQuery(filters);

  const canDashboard = can(scope.data, 'dashboard:read');
  const canReport = can(scope.data, 'report:read');
  const canFraud = can(scope.data, 'fraud:read');

  /*
   * The immediately preceding window of equal length, for the KPI deltas.
   *
   * Derived from the same two dates rather than assuming 30 days: if the manager
   * asked for 7 days, "vs the previous period" has to mean the 7 before it or
   * the comparison is meaningless. One extra call to an endpoint that already
   * exists — no new API surface for this.
   */
  const span = Math.max(daysBetween(filters.from, filters.to), 1);
  const previousFrom = toDateInput(new Date(Date.parse(startOfDayIso(filters.from)) - span * 86_400_000));
  const previousQuery = { ...query, from: startOfDayIso(previousFrom), to: startOfDayIso(filters.from) };

  /* --------------------------------------------------------- above the fold */

  const activity = useQuery({
    queryKey: ['activity', 'overview', query.regionId],
    queryFn: () =>
      api.get<Page<ActivityRow>>(
        withQuery('/reporting/activity', {
          page: 1,
          pageSize: 8,
          // Longest-running first. The endpoint defaults to newest, but a
          // supervisor acts on the visit that has been open three hours, not on
          // the one that started a minute ago.
          sort: 'minutesElapsed' satisfies ActivitySortKey,
          dir: 'desc',
          regionId: query.regionId,
        }),
      ),
    enabled: canDashboard,
    refetchInterval: 30_000,
    // The 30-second tick would otherwise blank the table on every refresh.
    placeholderData: keepPrevious,
  });

  const summary = useQuery({
    queryKey: ['summary', key],
    queryFn: () => api.get<ReportSummary>(withQuery('/reporting/summary', query)),
    enabled: enabled && canDashboard,
    staleTime: SUMMARY_STALE,
    placeholderData: keepPrevious,
  });

  const previousSummary = useQuery({
    queryKey: ['summary', 'previous', key],
    queryFn: () => api.get<ReportSummary>(withQuery('/reporting/summary', previousQuery)),
    enabled: enabled && canDashboard,
    staleTime: SUMMARY_STALE,
    placeholderData: keepPrevious,
  });

  const planVsActual = useQuery({
    queryKey: ['plan-vs-actual', key],
    queryFn: () => api.get<PlanVsActual>(withQuery('/reporting/plan-vs-actual', query)),
    enabled: enabled && canDashboard,
    staleTime: SUMMARY_STALE,
    placeholderData: keepPrevious,
  });

  const trend = useQuery({
    queryKey: ['trend', key],
    queryFn: () => api.get<VisitTrendPoint[]>(withQuery('/reporting/trend', query)),
    enabled: enabled && canDashboard,
    staleTime: SUMMARY_STALE,
    placeholderData: keepPrevious,
  });

  /* --------------------------------------------------------- below the fold */

  const deferred = belowFold && enabled;

  const leaderboard = useQuery({
    queryKey: ['leaderboard', 'overview', key],
    queryFn: () =>
      api.get<Page<LeaderboardRow>>(
        // Sorted by coverage, matching the column the panel actually renders
        // and the measure its target badge is computed from.
        withQuery('/reporting/leaderboard', {
          ...query,
          page: 1,
          pageSize: 5,
          sort: 'outletsVisited',
          dir: 'desc',
        }),
      ),
    enabled: deferred && canDashboard,
    staleTime: SUMMARY_STALE,
    placeholderData: keepPrevious,
  });

  const planogramByChannel = useQuery({
    queryKey: ['planogram-channel', key],
    queryFn: () =>
      api.get<Page<PlanogramChannelRow>>(
        withQuery('/reporting/planogram/by-channel', {
          ...query,
          page: 1,
          pageSize: 8,
          sort: 'averageScore',
          dir: 'desc',
        }),
      ),
    enabled: deferred && canReport,
    staleTime: SUMMARY_STALE,
    placeholderData: keepPrevious,
  });

  const worstOutlets = useQuery({
    queryKey: ['planogram-worst', key],
    queryFn: () =>
      api.get<Page<PlanogramReportRow>>(
        withQuery('/reporting/planogram', {
          ...query,
          page: 1,
          pageSize: 5,
          // Ascending: the five WORST outlets. The actionable end of the list,
          // and the panel header says so, so it is never read as a top five.
          sort: 'averageScore',
          dir: 'asc',
        }),
      ),
    enabled: deferred && canReport,
    staleTime: SUMMARY_STALE,
    placeholderData: keepPrevious,
  });

  const priceByRegion = useQuery({
    queryKey: ['price-region', key],
    queryFn: () =>
      api.get<Page<PriceIndexRegionRow>>(
        withQuery('/price-index/by-region', {
          from: query.from,
          to: query.to,
          regionId: query.regionId,
          page: 1,
          pageSize: 5,
          sort: 'observations',
          dir: 'desc',
        }),
      ),
    enabled: deferred && canReport,
    staleTime: SUMMARY_STALE,
    placeholderData: keepPrevious,
  });

  const priceBySku = useQuery({
    queryKey: ['price-sku', key],
    queryFn: () =>
      api.get<Page<PriceIndexRow>>(
        withQuery('/price-index/summary', {
          from: query.from,
          to: query.to,
          regionId: query.regionId,
          page: 1,
          pageSize: 5,
          sort: 'observations',
          dir: 'desc',
        }),
      ),
    enabled: deferred && canReport,
    staleTime: SUMMARY_STALE,
    placeholderData: keepPrevious,
  });

  const flags = useQuery({
    queryKey: ['fraud-flags', 'overview', key],
    queryFn: () =>
      api.get<Page<FraudFlagRow>>(
        withQuery('/fraud/flags', {
          // BOTH open states. A flag somebody moved to "tindak lanjut" is still
          // outstanding work; counting only PENDING made the panel report an
          // empty queue the moment every case had been picked up and none had
          // been finished.
          disposition: 'PENDING,FOLLOW_UP',
          from: query.from,
          to: query.to,
          regionId: query.regionId,
          page: 1,
          pageSize: 4,
          sort: 'visitDate',
          // Oldest first, matching `/fraud`. The panel is a preview of that
          // queue, and a preview ordered opposite to the thing it previews
          // shows four flags the supervisor will not see until the last page.
          dir: 'asc',
        }),
      ),
    enabled: deferred && canFraud,
    staleTime: SUMMARY_STALE,
    placeholderData: keepPrevious,
  });

  return {
    invalid,
    enabled,
    previousLabel: `vs ${span} hari sebelumnya`,
    scope,
    activity,
    summary,
    previousSummary,
    planVsActual,
    trend,
    leaderboard,
    planogramByChannel,
    worstOutlets,
    priceByRegion,
    priceBySku,
    flags,
  };
}
